import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ChevronDown, ChevronRight, FileDown, PenLine, Percent } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import {
  BoutonSecondaire,
  Carte,
  Chargement,
  EnteteEcran,
  MessageErreur,
  Pastille,
} from '../components/ui/Primitives';
import { BarreSites } from '../components/ui/BarreSites';
import { Modale } from '../components/ui/Modale';
import { BoutonMint } from '../components/ui/Primitives';
import { usePerimetre } from '../site/usePerimetre';
import { useAuth } from '../auth/useAuth';
import { ErreurApi } from '../lib/api';
import {
  useCloturerSeance,
  useSeances,
  useSignerFormateur,
  useSignerPresence,
  type DemiJournee,
  type SeanceAffichee,
} from '../lib/useEmargement';
import {
  useOuvrirDemiJournee,
  useRouvrirAppel,
  useSessionsEmargeables,
  useSortirModeSignature,
  useValiderAppel,
} from '../lib/useEmargementSession';
import { SelectionSession } from '../components/emargement/SelectionSession';
import { AppelSession } from '../components/emargement/AppelSession';
import { SuiviEmargement } from '../components/emargement/SuiviEmargement';
import { ModeSignature } from '../components/emargement/ModeSignature';
import { SeancesHorsSession, type CreationPreremplie } from '../components/emargement/SeancesHorsSession';
import { ModaleDetailPresence } from '../components/emargement/ModaleDetailPresence';
import { ModaleSignatureFormateur } from '../components/emargement/ModaleSignatureFormateur';
import { ModaleExportAudit } from '../components/emargement/ModaleExportAudit';
import { FeuilleEmargement } from '../components/documents/FeuilleEmargement';
import { ReouvertureFeuille } from '../components/emargement/ReouvertureFeuille';
import { useValidationDirecte } from '../components/modifications-sensibles/ElementsDemande';
import {
  EtatMonEmargement,
  HistoriqueMonEmargement,
  LienRelevePresence,
  ProchainesMonEmargement,
  ProgressionMonEmargement,
  SyntheseMonEmargement,
} from '../components/emargement/MonEmargement';
import { useMonEmargement } from '../lib/useEspaceApprenant';
import { CHEMIN_ABSENTEISME, peutLireAbsenteisme } from '../lib/useAbsenteisme';
import { BoutonJustifierAbsence, MesJustificatifs } from '../components/emargement/JustifierAbsence';
import { NotificationsAppareil } from '../components/notifications/NotificationsAppareil';

/**
 * Écran « Émargement » (CDC §6.7).
 *
 * C'est la feuille d'émargement papier que cet écran remplace, et le parcours
 * suit le même geste, dans le même ordre :
 *
 *   Formation → Session → Journée → Matin / Après-midi → Appel → Signatures
 *
 * Il est écrit pour la tablette qu'on pose sur une table de salle, pas pour un
 * poste de bureau : grandes cibles, un apprenant à la fois, passage automatique
 * au suivant, et un mode signature dépouillé pendant que la tablette circule.
 *
 * Deux choses n'y sont volontairement **pas** :
 *
 * — un second calendrier. Les journées viennent de la « Programmation du
 *   calendrier » de `/adm-formations`, que `/calendar` lit déjà ;
 * — une liste de convocation. Les apprenants attendus sont ceux que leur
 *   dossier rattache à la session.
 *
 * L'apprenant connecté ne conduit rien : il voit sa propre feuille du jour et y
 * signe. La conduite de la séance est gardée côté API, pas seulement masquée ici.
 *
 * Le tableau de bord du formateur y mène directement (TF-456) :
 * `?session=…&demi=MATIN` ouvre — ou reprend — la feuille de la demi-journée du
 * jour, le geste même de la sélection ; `?seance=…` déplie les séances hors
 * session sur celle-ci ; `?creer=<formation>&site=…&demi=…` y ouvre la
 * création pré-remplie de la feuille d'une promotion d'apprentissage, que le
 * formateur confirme. L'adresse est vidée une fois lue : la recharger ne
 * rejoue rien.
 */

type Etape = 'selection' | 'appel' | 'suivi' | 'signature';

function jourCourant(): string {
  return new Intl.DateTimeFormat('fr-CA', { timeZone: 'Indian/Reunion' }).format(new Date());
}

function messageErreur(erreur: unknown, defaut: string): string | null {
  if (erreur === null || erreur === undefined) return null;
  return erreur instanceof ErreurApi ? erreur.message : defaut;
}

export function EmargementPage(): JSX.Element {
  const { utilisateur } = useAuth();
  const estApprenant = utilisateur?.profil === 'APPRENANT';

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      {estApprenant ? <FeuilleDeLApprenant /> : <ParcoursEnSalle />}
    </div>
  );
}

/**
 * Vue de l'apprenant connecté (CDC §8.1) : la feuille du jour où il signe, puis
 * tout son émargement — synthèse, seuil d'absences, historique et prochaines
 * demi-journées. L'API ne lui rend que les séances où il est lui-même attendu,
 * et la synthèse est celle que l'équipe lit dans son dossier.
 */
function FeuilleDeLApprenant(): JSX.Element {
  const { site } = usePerimetre();
  const client = useQueryClient();
  const [jour, setJour] = useState(jourCourant);
  const { data, isLoading, error } = useSeances(site, jour);
  const emargement = useMonEmargement();
  const signer = useSignerPresence();

  const seances = data?.seances ?? [];

  return (
    <>
      <EnteteEcran
        titre="Mon émargement"
        sousTitre="Signez votre présence et consultez votre historique"
        actions={
          <>
            <input
              type="date"
              value={jour}
              onChange={(evenement) => setJour(evenement.target.value)}
              aria-label="Journée consultée"
              className="min-h-[44px] rounded-xl border border-gray-200 bg-white px-3 text-sm"
            />
            <LienRelevePresence />
          </>
        }
      />

      <EtatMonEmargement isLoading={emargement.isLoading} error={emargement.error} />
      {emargement.data && <SyntheseMonEmargement donnees={emargement.data} />}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="min-w-0 space-y-6 lg:col-span-2">
          {error && (
            <MessageErreur
              message={messageErreur(error, 'Vos séances n’ont pas pu être chargées.')!}
            />
          )}
          {signer.error !== null && (
            <MessageErreur message={messageErreur(signer.error, 'Signature refusée.')!} />
          )}
          {isLoading && <Chargement libelle="Chargement de vos séances…" />}

          {!isLoading && seances.length === 0 && (
            <Carte titre="Séquence du jour" sansBordure>
              <p className="p-4 text-sm text-codialis-gray sm:p-6">
                Aucune demi-journée ne vous attend à cette date.
              </p>
            </Carte>
          )}

          {seances.map((seance) => (
            <FeuilleEmargement
              key={seance.id}
              seance={seance}
              enCours={signer.isPending}
              onSigner={(presenceId, trace) =>
                signer.mutate(
                  { seanceId: seance.id, presenceId, trace },
                  {
                    // La signature change la synthèse et l'historique.
                    onSuccess: () =>
                      void client.invalidateQueries({ queryKey: ['espace-apprenant'] }),
                  },
                )
              }
            />
          ))}

          {emargement.data && <HistoriqueMonEmargement lignes={emargement.data.historique} />}
        </div>

        {emargement.data && (
          <div className="min-w-0 space-y-6">
            <NotificationsAppareil />
            <ProgressionMonEmargement donnees={emargement.data} action={<BoutonJustifierAbsence />} />
            <MesJustificatifs />
            <ProchainesMonEmargement lignes={emargement.data.prochaines} />
          </div>
        )}
      </div>
    </>
  );
}

function demiJourneeDemandee(valeur: string | null): DemiJournee | null {
  return valeur === 'MATIN' || valeur === 'APRES_MIDI' ? valeur : null;
}

const HEURE = /^([01]\d|2[0-3]):[0-5]\d$/;

/** Création pré-remplie demandée par l'adresse, ou `null` ; l'API juge la création elle-même. */
function creationDemandee(parametres: URLSearchParams): CreationPreremplie | null {
  const formationCode = parametres.get('creer');
  const site = parametres.get('site');
  const demiJournee = demiJourneeDemandee(parametres.get('demi'));
  if (!formationCode || !site || !demiJournee) return null;
  const debut = parametres.get('debut') ?? '';
  const fin = parametres.get('fin') ?? '';
  return {
    formationCode,
    site,
    demiJournee,
    ...(HEURE.test(debut) && HEURE.test(fin) ? { heureDebut: debut, heureFin: fin } : {}),
    libelle: (parametres.get('libelle') ?? '').slice(0, 200),
  };
}

function ParcoursEnSalle(): JSX.Element {
  const { site } = usePerimetre();
  const { utilisateur } = useAuth();
  const [parametres, setParametres] = useSearchParams();
  // Accès direct depuis le tableau de bord du formateur, lu une seule fois.
  const [demande] = useState(() => ({
    sessionId: parametres.get('session'),
    demiJournee: demiJourneeDemandee(parametres.get('demi')),
    seanceId: parametres.get('seance'),
    creation: creationDemandee(parametres),
  }));
  const demandeTraitee = useRef(false);
  const carteHorsSession = useRef<HTMLDivElement>(null);
  const [jour, setJour] = useState(jourCourant);
  const [etape, setEtape] = useState<Etape>('selection');
  const [seanceCourante, setSeanceCourante] = useState<SeanceAffichee | null>(null);
  const [horsSession, setHorsSession] = useState(demande.seanceId !== null || demande.creation !== null);
  const [avis, setAvis] = useState<string | null>(null);
  // Reprise de l'appel : elle rouvre une feuille déjà validée, donc elle
  // redemande le code posé quand la tablette a quitté les mains du formateur.
  const [reouverture, setReouverture] = useState(false);
  const [codeReprise, setCodeReprise] = useState('');
  // Détail d'un apprenant (signature, horaires réels, correction, historique)
  // et réouverture d'une feuille clôturée.
  const [detailId, setDetailId] = useState<string | null>(null);
  const [reouvertureFeuille, setReouvertureFeuille] = useState(false);
  const [signatureFormateur, setSignatureFormateur] = useState(false);
  const [exportAudit, setExportAudit] = useState(false);

  const directe = useValidationDirecte();
  const sessions = useSessionsEmargeables(site, jour, etape === 'selection');
  const ouvrir = useOuvrirDemiJournee();
  const validerAppel = useValiderAppel();
  const rouvrir = useRouvrirAppel();
  const signer = useSignerPresence();
  const sortir = useSortirModeSignature();
  const signerFormateur = useSignerFormateur();
  const cloturer = useCloturerSeance();

  // Changer de jour ou d'antenne pendant un émargement laisserait à l'écran une
  // feuille qui n'appartient plus au périmètre affiché.
  useEffect(() => {
    setEtape('selection');
    setSeanceCourante(null);
    setAvis(null);
    setDetailId(null);
  }, [site, jour]);

  /**
   * File de circulation de la tablette : les présents et les retards qui n'ont
   * pas encore signé, dans l'ordre alphabétique de la feuille. Elle se recalcule
   * à chaque signature, si bien que l'apprenant qui vient de signer en sort et
   * que le suivant prend sa place.
   */
  const file = useMemo(() => {
    if (!seanceCourante) return [];
    return seanceCourante.presences
      .filter(
        (presence) =>
          presence.signeLe === null &&
          (presence.statut === 'PRESENT' || presence.statut === 'RETARD'),
      )
      .map((presence) => ({
        presenceId: presence.id,
        apprenantId: presence.apprenantId,
        apprenant: presence.apprenant,
      }));
  }, [seanceCourante]);

  function ouvrirCycle(sessionId: string, demiJournee: DemiJournee): void {
    setAvis(null);
    ouvrir.mutate(
      { sessionId, date: jour, demiJournee },
      {
        onSuccess: (resultat) => {
          setSeanceCourante(resultat.seance);
          // Une feuille dont l'appel est déjà validé se reprend au suivi, pas à
          // l'appel : c'est le cas de la tablette rouverte en cours de séance.
          // Une feuille clôturée se relit au suivi, appel fait ou non.
          setEtape(resultat.seance.appelValideLe || resultat.seance.statut === 'CLOTUREE' ? 'suivi' : 'appel');
          if (!resultat.creee) {
            setAvis(
              resultat.ajoutes > 0
                ? `Émargement repris. ${resultat.ajoutes} apprenant(s) rattaché(s) depuis ont été ajoutés à la feuille.`
                : 'Émargement repris là où il s’était arrêté.',
            );
          }
        },
      },
    );
  }

  useEffect(() => {
    if (demandeTraitee.current) return;
    demandeTraitee.current = true;
    if (!demande.sessionId && !demande.seanceId && !parametres.get('creer')) return;
    setParametres({}, { replace: true });
    if (demande.sessionId && demande.demiJournee) {
      ouvrirCycle(demande.sessionId, demande.demiJournee);
    } else if (demande.seanceId) {
      // Une création pré-remplie amène elle-même son formulaire à l'écran.
      carteHorsSession.current?.scrollIntoView({ block: 'start' });
    }
    // La demande se lit une fois, au premier affichage.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const erreurEtape =
    messageErreur(ouvrir.error, 'La feuille n’a pas pu être ouverte.') ??
    messageErreur(validerAppel.error, 'L’appel n’a pas pu être validé.') ??
    messageErreur(rouvrir.error, 'L’appel n’a pas pu être rouvert.');

  if (etape === 'signature' && seanceCourante) {
    return (
      <ModeSignature
        seance={seanceCourante}
        file={file}
        enCours={signer.isPending}
        erreur={messageErreur(signer.error, 'Signature refusée.')}
        sortieEnCours={sortir.isPending}
        erreurSortie={messageErreur(sortir.error, 'Code refusé.')}
        onSigner={(presenceId, trace) =>
          signer.mutate(
            { seanceId: seanceCourante.id, presenceId, trace },
            { onSuccess: (resultat) => setSeanceCourante(resultat.seance) },
          )
        }
        onSortir={(pin) =>
          sortir.mutate(
            { seanceId: seanceCourante.id, pin },
            { onSuccess: () => setEtape('suivi') },
          )
        }
      />
    );
  }

  return (
    <>
      <EnteteEcran
        titre="Émargement"
        sousTitre="Appel puis signatures, par demi-journée · conçu pour la tablette en salle"
        actions={
          <>
            <input
              type="date"
              value={jour}
              onChange={(evenement) => setJour(evenement.target.value)}
              aria-label="Journée émargée"
              className="min-h-[44px] rounded-xl border border-gray-200 bg-white px-3 text-sm"
            />
            <BoutonSecondaire onClick={() => setExportAudit(true)}>
              <FileDown className="mr-1.5 h-4 w-4" aria-hidden="true" />
              Export PDF audit
            </BoutonSecondaire>
            {peutLireAbsenteisme(utilisateur?.profil) && (
              <Link
                to={CHEMIN_ABSENTEISME}
                className="inline-flex min-h-[44px] items-center rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-codialis-navy transition hover:bg-gray-50"
              >
                <Percent className="mr-1.5 h-4 w-4" aria-hidden="true" />
                Taux d’absentéisme
              </Link>
            )}
          </>
        }
      />

      <ModaleExportAudit ouverte={exportAudit} jour={jour} onFermer={() => setExportAudit(false)} />

      <BarreSites />

      {avis && (
        <div className="rounded-2xl bg-codialis-sand p-4 text-sm text-codialis-navy">{avis}</div>
      )}
      {erreurEtape && <MessageErreur message={erreurEtape} />}
      {cloturer.data && (
        <div className="rounded-2xl bg-codialis-sand p-4 text-sm text-codialis-navy">
          {cloturer.data.message}
        </div>
      )}
      {cloturer.error !== null && (
        <MessageErreur message={messageErreur(cloturer.error, 'Clôture refusée.')!} />
      )}

      {etape === 'selection' && (
        <SelectionSession
          sessions={sessions.data?.sessions ?? []}
          chargement={sessions.isLoading || ouvrir.isPending}
          erreur={messageErreur(sessions.error, 'Les sessions n’ont pas pu être chargées.')}
          onOuvrir={ouvrirCycle}
        />
      )}

      {etape === 'appel' && seanceCourante && (
        <AppelSession
          seance={seanceCourante}
          enCours={validerAppel.isPending}
          erreur={messageErreur(validerAppel.error, 'L’appel n’a pas pu être validé.')}
          onRetour={() => {
            setSeanceCourante(null);
            setEtape('selection');
          }}
          onValider={(pointages, pin) =>
            validerAppel.mutate(
              { seanceId: seanceCourante.id, pointages, pin },
              {
                onSuccess: (resultat) => {
                  setSeanceCourante(resultat.seance);
                  setAvis(resultat.message);
                  // Sans personne à faire signer, le mode signature n'aurait
                  // rien à montrer : on va droit au suivi.
                  setEtape(resultat.aSigner.length > 0 ? 'signature' : 'suivi');
                },
              },
            )
          }
        />
      )}

      {etape === 'suivi' && seanceCourante && (
        <SuiviEmargement
          seance={seanceCourante}
          onRetour={() => {
            setSeanceCourante(null);
            setEtape('selection');
          }}
          onModeSignature={() => setEtape('signature')}
          onRouvrirAppel={() => {
            setCodeReprise('');
            setReouverture(true);
          }}
          onDetail={setDetailId}
          onRouvrirFeuille={() => setReouvertureFeuille(true)}
          onSeanceMiseAJour={setSeanceCourante}
          actions={
            <>
              {seanceCourante.formateurSigneLe === null && seanceCourante.statut !== 'CLOTUREE' && (
                <BoutonSecondaire
                  onClick={() => {
                    signerFormateur.reset();
                    setSignatureFormateur(true);
                  }}
                >
                  <PenLine className="mr-1.5 h-4 w-4" aria-hidden="true" />
                  Signature du formateur
                </BoutonSecondaire>
              )}
              {seanceCourante.statut !== 'CLOTUREE' && (
                <BoutonSecondaire
                  onClick={() =>
                    cloturer.mutate(seanceCourante.id, {
                      onSuccess: (resultat) => setSeanceCourante(resultat.seance),
                    })
                  }
                  disabled={cloturer.isPending}
                >
                  Clôturer et archiver le PV
                </BoutonSecondaire>
              )}
            </>
          }
        />
      )}

      <Modale
        ouverte={reouverture}
        titre="Reprendre l’appel"
        onFermer={() => setReouverture(false)}
      >
        <div className="space-y-3 p-4 sm:p-6">
          <p className="text-sm text-codialis-gray">
            Rouvrir l’appel permet de repointer toute la salle. Saisissez le{' '}
            {seanceCourante?.codeSourceLibelle ?? 'code'} à quatre chiffres. Pour corriger un seul
            apprenant — arrivée tardive, départ anticipé — ouvrez plutôt son détail.
          </p>
          <input
            type="password"
            inputMode="numeric"
            autoComplete="off"
            maxLength={4}
            value={codeReprise}
            onChange={(evenement) => setCodeReprise(evenement.target.value.replace(/\D/g, ''))}
            aria-label="Code de l’appel"
            className="min-h-[56px] w-40 rounded-xl border-2 border-gray-200 text-center font-display text-2xl tracking-[0.5em] text-codialis-navy"
          />
          {rouvrir.error !== null && (
            <MessageErreur message={messageErreur(rouvrir.error, 'Code refusé.')!} />
          )}
          <div className="flex flex-wrap gap-2">
            <BoutonMint
              onClick={() => {
                if (!seanceCourante) return;
                rouvrir.mutate(
                  { seanceId: seanceCourante.id, pin: codeReprise || undefined },
                  {
                    onSuccess: (seance) => {
                      setSeanceCourante(seance);
                      setReouverture(false);
                      setEtape('appel');
                    },
                  },
                );
              }}
              disabled={rouvrir.isPending}
            >
              {rouvrir.isPending ? 'Vérification…' : 'Rouvrir l’appel'}
            </BoutonMint>
            <BoutonSecondaire onClick={() => setReouverture(false)}>Annuler</BoutonSecondaire>
          </div>
        </div>
      </Modale>

      {seanceCourante && (
        <ModaleSignatureFormateur
          seance={seanceCourante}
          ouverte={signatureFormateur}
          enCours={signerFormateur.isPending}
          erreur={messageErreur(signerFormateur.error, 'Signature refusée.')}
          onFermer={() => setSignatureFormateur(false)}
          onSigner={(trace) =>
            signerFormateur.mutate(
              { seanceId: seanceCourante.id, trace },
              {
                onSuccess: (seance) => {
                  setSeanceCourante(seance);
                  setSignatureFormateur(false);
                },
              },
            )
          }
        />
      )}

      {seanceCourante && (
        <ModaleDetailPresence
          seance={seanceCourante}
          presenceId={detailId}
          onFermer={() => setDetailId(null)}
          onSeanceMiseAJour={setSeanceCourante}
        />
      )}

      <Modale
        ouverte={reouvertureFeuille}
        titre={directe ? 'Rouvrir l’émargement' : 'Demander la réouverture'}
        onFermer={() => setReouvertureFeuille(false)}
      >
        <div className="p-4 sm:p-6">
          {seanceCourante && (
            <ReouvertureFeuille
              seance={seanceCourante}
              onAnnuler={() => setReouvertureFeuille(false)}
              onTermine={(seance, message) => {
                setSeanceCourante(seance);
                setReouvertureFeuille(false);
                setAvis(message);
              }}
            />
          )}
        </div>
      </Modale>

      <div ref={carteHorsSession} className="scroll-mt-24">
        <Carte
          titre="Séances hors session"
          actions={
            <>
              <Pastille texte="E-learning · rattrapage" ton="neutral" />
              <BoutonSecondaire onClick={() => setHorsSession((ouvert) => !ouvert)}>
                {horsSession ? (
                  <ChevronDown className="mr-1.5 h-4 w-4" aria-hidden="true" />
                ) : (
                  <ChevronRight className="mr-1.5 h-4 w-4" aria-hidden="true" />
                )}
                {horsSession ? 'Replier' : 'Ouvrir'}
              </BoutonSecondaire>
            </>
          }
          sansBordure
        >
          {horsSession ? (
            <div className="p-4 sm:p-6">
              <SeancesHorsSession seanceInitiale={demande.seanceId} creationInitiale={demande.creation} />
            </div>
          ) : (
            <p className="p-4 text-sm text-codialis-gray sm:p-6">
              Séances sans groupe attitré — e-learning, rattrapage, module réunissant deux
              sessions. Elles s’écrivent dans les mêmes feuilles que le parcours en salle.
            </p>
          )}
        </Carte>
      </div>
    </>
  );
}

